import axios, { AxiosError } from 'axios';

import { ServiceResponse } from '../types/service-response.type';
import { PaginatedQuery } from '../types/paginated-query.type';
import { CompanyCreation } from '../types/company-creation.type';
import { VehicleCreation } from '../types/vehicle.type';

import { addQueryParams, getAuthToken, handleResponseError } from './service';

type QueryParams = {
    status: number;
} & PaginatedQuery;

const USERS_BASE_URL = process.env.REACT_APP_USERS_URL;

const getAll = async (queryParams?: QueryParams): Promise<ServiceResponse> => {
    console.log('User Service: getting all users with query params');
    try {
        const inlineParams = addQueryParams(queryParams);
        const response = await axios.get(
            `${USERS_BASE_URL}/api/v1/users${inlineParams}`,
            { headers: { Authorization: getAuthToken() } }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const getById = async (
    id: number,
    includes?: string[]
): Promise<ServiceResponse> => {
    console.log('User Service: getting user by id');
    try {
        const params = addQueryParams({ includes: includes?.join(',') });
        const response = await axios.get(
            `${USERS_BASE_URL}/api/admin/users/${id}${params}`,
            {
                headers: { Authorization: getAuthToken() },
            }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const update = async (id: number, data: any): Promise<ServiceResponse> => {
    console.log('User Service: updating user by id');
    try {
        const response = await axios.put(
            `${USERS_BASE_URL}/api/admin/users/${id}`,
            data,
            { headers: { Authorization: getAuthToken() } }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const search = async (name: string): Promise<ServiceResponse> => {
    console.log('User Service: searching by name');
    try {
        const response = await axios.get(
            `${USERS_BASE_URL}/api/v1/users?search=${name}`,
            { headers: { Authorization: getAuthToken() } }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const searchCompanyByName = async (name: string): Promise<ServiceResponse> => {
    console.log('User Service: searching company by name');
    try {
        const response = await axios.get(
            `${USERS_BASE_URL}/api/admin/companies/search?name=${name}`,
            { headers: { Authorization: getAuthToken() } }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const getCompanies = async (params: any): Promise<ServiceResponse> => {
    console.log('User Service: fetch companies');
    try {
        const inlineParams = addQueryParams(params);
        const response = await axios.get(
            `${USERS_BASE_URL}/api/admin/companies${inlineParams}`,
            {
                headers: { Authorization: getAuthToken() },
            }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const getCompanyDetails = async (id: number): Promise<ServiceResponse> => {
    console.log('User Service: fetch company details');
    try {
        const response = await axios.get(
            `${USERS_BASE_URL}/api/admin/companies/${id}`,
            {
                headers: { Authorization: getAuthToken() },
            }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const createCompany = async (
    data: CompanyCreation
): Promise<ServiceResponse> => {
    console.log('User Service: fetch company createCompany');
    try {
        const response = await axios.post(
            `${USERS_BASE_URL}/api/admin/companies`,
            data,
            {
                headers: { Authorization: getAuthToken() },
            }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const getCompanyUsers = async (id: number): Promise<ServiceResponse> => {
    console.log('User Service: fetch company getCompanyUsers');
    try {
        const response = await axios.get(
            `${USERS_BASE_URL}/api/admin/companies/${id}/users`,
            {
                headers: { Authorization: getAuthToken() },
            }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const addVehicle = async (
    userId: number,
    vehicle: VehicleCreation
): Promise<ServiceResponse> => {
    console.log('User Service: add vehicle to user');
    try {
        const response = await axios.post(
            `${USERS_BASE_URL}/api/admin/users/${userId}/vehicles`,
            vehicle,
            {
                headers: { Authorization: getAuthToken() },
            }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

type UserCreation = {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    documentType: string;
    document: string;
};

const createUserByAdmin = async (
    data: UserCreation
): Promise<ServiceResponse> => {
    console.log('User Service: create user by admin');
    try {
        const response = await axios.post(
            `${USERS_BASE_URL}/api/admin/users`,
            data,
            {
                headers: { Authorization: getAuthToken() },
            }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const checkPasswordToken = async (token: string): Promise<ServiceResponse> => {
    console.log('Check recovery password token');
    try {
        const response = await axios.get(
            `${USERS_BASE_URL}/api/admin/operators/check-password-token?token=${token}`
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const changeOperatorPasswordByToken = async (data: {
    token: string;
    password: string;
}) => {
    console.log('Change operator password by token');
    try {
        const response = await axios.post(
            `${USERS_BASE_URL}/api/admin/operators/change-password-by-token`,
            data
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

export const userSdk = {
    getAll,
    getById,
    update,
    search,
    getCompanies,
    getCompanyDetails,
    createCompany,
    getCompanyUsers,
    addVehicle,
    searchCompanyByName,
    createUserByAdmin,
    checkPasswordToken,
    changeOperatorPasswordByToken,
};
